import React, { useCallback } from 'react';
import { cn } from '@express-labs/raven-tools';
import { Image, Slide } from 'pure-react-carousel';
import LazyImage from '../../../LazyImage/LazyImage';
import FallbackImage from '../../../LazyImage/subcomponents/FallbackImage/FallbackImage';
import Button from '../../../DeprecatedLink/DeprecatedLink';
import s from './RenderSlideCategories.scss';

export interface RenderSlideCategoriesProps {
  /** A unique categoryCarId. This number is generated in raven-server. */
  categoryCardId: string;

  /** This supplies the text for a "call to action" link */
  ctaText?: string;

  /** Defines the device width. string: desktop or mobile. Passed in by Carousel */
  deviceWidth: number;

  /** The array index number of this slide.  This same number is used in Dot, and thumbnail buttons
  to link them together.  For example, clicking on Dot 3 moves slide 3 into the leftmost
  slot in the Carousel viewport.  See pure-react-carousel documentation for more info.
  This number is generated automatically in Carousel. */
  i: number;

  /** Defers loading of images that are not initially visible in the Carousel */
  isLazyLoaded?: boolean;

  /** A url to a "normal sized" image. Not a thumbnail, and not a large image.
  This is "optional" for backwards compatibility, but as of 11/12/18 we should be using
  this to avoid "fuzzy images" due to the browser downsampling a large image into a small
  space. */
  main: string;

  /** Action handler for clicks on the image. Passed in by Carousel. */
  onClick: (i: string, ev: React.MouseEvent<HTMLElement>) => void;

  /** Relative size of the slide, this value is represented as a percentage. Passed in by Carousel.
  This value is determined in CarouselCategories.
  Required to calculate the width of every slide in inline-style. */
  size: number;

  /** The number of total slides on the carousel-category. Passed in by Carousel.
  Required to calculate the width of every slide in inline-style. */
  totalSlides: number;

  /** A unique id for the carousel. This number is used to help create unique css id's and class
  names so that you can have more than one carousel on the page and style them differently,
  or reference a dom element via their unique id attribute name via javascript.
  This number is generated automatically in Carousel and passed in. */
  uid: number;
}

const RenderSlideCategories = ({
  categoryCardId,
  ctaText,
  deviceWidth,
  i,
  isLazyLoaded = false,
  main,
  onClick,
  size,
  uid,
}: RenderSlideCategoriesProps) => {
  const handleOnClick = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      onClick(categoryCardId, ev);
    },
    [categoryCardId, onClick]
  );

  // Pure React Carousel already tests for `<Image />` component image load errors.
  // Also, it's to hard to target the external library and trigger the error.
  /* istanbul ignore next */
  const catchImageLoadError = useCallback(
    () => <FallbackImage alt={main} />,
    [main]
  );

  const slideClassName =
    deviceWidth > 768 ? s.slideCardDesktop : s.slideCardMobile;
  const ctaLinkClassName =
    deviceWidth > 768 ? s.ctaLinkDesktop : s.ctaLinkMobile;

  return (
    <div
      className={cn(s.slideCard, slideClassName)}
      style={{
        width: `${size}%`,
      }}
    >
      <Button
        tabIndex={0}
        onClick={handleOnClick}
        className={cn(s.wrapperLink, 'focus-on-modal-close')}
      >
        <Slide
          id={`carousel__slide--${uid}--${i}`}
          index={i}
          tag="div"
          className={s.slideContainer}
          innerClassName={s.slideInner}
          tabIndex={-1}
          // The div wrapping this entire component is replacing Pure React
          // Carousel's "Slide" component and providing the width of the slide.
          // so that we can have different widths of slides (single, double, tripple)
          // Pure React Carousel adds width to the Slide component. So we must
          // overwrite the styles created by Pure React Carousel
          style={{ width: '100%', paddingBottom: 'unset' }}
        >
          {isLazyLoaded ? (
            <LazyImage src={main} width={300} alt={main} />
          ) : (
            <Image
              className={s.heightOverride}
              tag="img"
              hasMasterSpinner={false}
              renderError={catchImageLoadError}
              src={main}
            />
          )}
        </Slide>
        <div className={cn(s.ctaLink, ctaLinkClassName)}>{ctaText}</div>
      </Button>
    </div>
  );
};

export default RenderSlideCategories;
